import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiUploadCloud } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PLAYLIST_GET, PLAYLIST_CREATE } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import MultiSelect from '../MultiSelect';
import './Page.css';
import './CreateCategory.css';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const AGE_GROUP_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

const THUMB_KEYS = [
  { key: 'small_thumbnail', label: 'Small thumbnail' },
  { key: 'thumbnail_url', label: 'Thumbnail URL' },
  { key: 'thumbnail_url_landscape', label: 'Thumbnail URL (landscape)' },
];

function CreatePlaylist() {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateId = location.state?.duplicateId;
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [age_groups, setAge_groups] = useState([]);
  const [popularity_score, setPopularity_score] = useState('');
  const [rank_score, setRank_score] = useState('');
  const [files, setFiles] = useState({ small_thumbnail: null, thumbnail_url: null, thumbnail_url_landscape: null });
  const [previews, setPreviews] = useState({ small_thumbnail: null, thumbnail_url: null, thumbnail_url_landscape: null });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(!!duplicateId);

  useEffect(() => {
    if (!duplicateId) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(PLAYLIST_GET.pathFor(duplicateId)), { method: PLAYLIST_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Category not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setTitle(data.title ?? '');
        setStatus(data.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');
        setAge_groups(Array.isArray(data.age_groups) ? data.age_groups : []);
        setPopularity_score(data.popularity_score != null ? String(data.popularity_score) : '');
        setRank_score(data.rank_score != null ? String(data.rank_score) : '');
        setPreviews({
          small_thumbnail: data.small_thumbnail ?? null,
          thumbnail_url: data.thumbnail_url ?? null,
          thumbnail_url_landscape: data.thumbnail_url_landscape ?? null,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load playlist to duplicate.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDuplicate(false);
      });
    return () => { cancelled = true; };
  }, [duplicateId]);

  useEffect(() => {
    return () => {
      THUMB_KEYS.forEach(({ key }) => {
        if (previews[key] && typeof previews[key] === 'string' && previews[key].startsWith('blob:')) {
          URL.revokeObjectURL(previews[key]);
        }
      });
    };
  }, [previews]);

  const handleFileChange = (key, e) => {
    const file = e.target.files?.[0];
    setError('');
    const prevPreview = previews[key];
    if (prevPreview && prevPreview.startsWith('blob:')) URL.revokeObjectURL(prevPreview);
    if (!file) {
      setFiles((f) => ({ ...f, [key]: null }));
      setPreviews((p) => ({ ...p, [key]: null }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select image files only (e.g. JPG, PNG).');
      setFiles((f) => ({ ...f, [key]: null }));
      setPreviews((p) => ({ ...p, [key]: null }));
      return;
    }
    setFiles((f) => ({ ...f, [key]: file }));
    setPreviews((p) => ({ ...p, [key]: URL.createObjectURL(file) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required.');
      return;
    }
    const pop = Number(popularity_score);
    const rank = Number(rank_score);
    if (Number.isNaN(pop) || pop < 0) {
      setError('Popularity score must be a valid number.');
      return;
    }
    if (Number.isNaN(rank) || rank < 0) {
      setError('Rank score must be a valid number.');
      return;
    }
    if (!files.small_thumbnail || !files.thumbnail_url || !files.thumbnail_url_landscape) {
      setError('All three thumbnails are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      formData.append('title', trimmedTitle);
      formData.append('status', status);
      formData.append('popularity_score', String(pop));
      formData.append('rank_score', String(rank));
      age_groups.forEach((v) => formData.append('age_groups', v));
      formData.append('small_thumbnail', files.small_thumbnail);
      formData.append('thumbnail_url', files.thumbnail_url);
      formData.append('thumbnail_url_landscape', files.thumbnail_url_landscape);

      const res = await fetch(apiUrl(PLAYLIST_CREATE.path), {
        method: PLAYLIST_CREATE.method,
        headers,
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }

      navigate('/dashboard/cms/playlist');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/playlist')}>
        <FiArrowLeft className="back-icon" aria-hidden />
          Back to Categories
      </button>

      <div className="create-category-header">
        <h3>{duplicateId ? 'Duplicate category' : 'Create category'}</h3>
        <p className="create-category-subtitle">
          {duplicateId
            ? 'Edit details below and upload new thumbnails to save as a new playlist.'
            : 'Add a new playlist with title, scores and thumbnails.'}
        </p>
      </div>

      {loadingDuplicate && <p className="create-category-loading">Loading playlist…</p>}

      <form onSubmit={handleSubmit} className="create-category-form" style={loadingDuplicate ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="playlist-title">Title <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="playlist-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Food"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="playlist-status">Status</label>
                <select id="playlist-status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isSubmitting}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single">
                <label htmlFor="playlist-popularity">Popularity score <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="playlist-popularity"
                  type="number"
                  min={0}
                  value={popularity_score}
                  onChange={(e) => setPopularity_score(e.target.value)}
                  placeholder="e.g. 86"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="playlist-rank">Rank score <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="playlist-rank"
                  type="number"
                  min={0}
                  value={rank_score}
                  onChange={(e) => setRank_score(e.target.value)}
                  placeholder="e.g. 215"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single form-row-full">
                <label>Age groups</label>
                <MultiSelect
                  options={AGE_GROUP_OPTIONS}
                  value={age_groups}
                  onChange={setAge_groups}
                  placeholder="Select age groups"
                  disabled={isSubmitting}
                  searchable={false}
                />
              </div>
            </div>
          </div>

          <div className="form-section form-section-full">
            <h4 className="form-section-title">Thumbnails <span className="label-required" aria-hidden="true">*</span></h4>
            <div className="form-grid-3">
              {THUMB_KEYS.map(({ key, label }) => (
                <div key={key} className="form-row-single">
                  <label>{label} <span className="label-required" aria-hidden="true">*</span></label>
                  <div className="thumbnail-upload-area">
                    <input type="file" id={`pl-upload-${key}`} accept="image/*" onChange={(e) => handleFileChange(key, e)} disabled={isSubmitting} className="thumbnail-input" />
                    <label htmlFor={`pl-upload-${key}`} className="thumbnail-upload-label">
                      <FiUploadCloud className="upload-icon" aria-hidden />
                      <span>{files[key] ? files[key].name : `Choose image`}</span>
                    </label>
                    {previews[key] && (
                      <div className="thumbnail-preview-wrap">
                        <img src={previews[key]} alt={`Preview ${label}`} className="thumbnail-preview-img" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/cms/playlist')} disabled={isSubmitting}>Cancel</button>
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create category'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreatePlaylist;
